import { createApp } from 'vue';
import i18n from '@common/common/i18n';
import '@common/cluster-common/src/assets/css/style.css';
import '@common/common/styles/css/reset.css';
import App from './App.vue';
import router from './router';
import { createPinia } from 'pinia';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import * as Sentry from '@sentry/vue';
import { useClusterInAppStore } from '@common/cluster-common';
import { setAnomalyReporter } from '@common/trend-report-common';
import useErrorStore from '@common/common/stores/error'; // 에러 스토어 불러오기
import { callWebViewReady, setCookieForDay } from '@common/common/utils';
import Cookies from 'js-cookie';

const domPurifySettings = {
    default: {
        ALLOWED_TAGS: ['b', 'span', 'p', 'font'],
        ALLOWED_ATTR: ['color', 'class', 'style'], // onclick, onerror 등은 금지
        FORBID_TAGS: ['style', 'img', 'iframe', 'svg', 'script'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick'],
    },
};

// 네이티브 브릿지 호출 여부 추적 (Sentry beforeSend + waitForToken 에서 사용)
// 이전 세션에서 남은 stale 쿠키 토큰과 구분하기 위해, "이번 세션에서 브릿지가 호출됐는지"를 따로 봐야 함.
declare global {
    interface Window {
        __nativeBridgeCalled?: boolean;
    }
}
window.__nativeBridgeCalled = false;

// 네이티브 브릿지 콜백이 useStore()를 호출하므로 pinia를 window 함수 정의 전에 활성화.
const app = createApp(App);
const pinia = createPinia();
app.use(pinia);

// 이전 세션의 stale declareDateTime 쿠키 제거.
// 이번 세션에 declareDateTime을 내려주지 않는 페이지(예: setToken 케이스)에서
// 이전 값이 fetch에 섞이는 것을 방지.
Cookies.remove('declareDateTimeValue');

// 쿠키 기반 전역 함수 정의
const applyNativeTokenData = (token: string | undefined, declareDateTime?: string, os?: string) => {
    window.__nativeBridgeCalled = true;
    const errorStore = useErrorStore();
    const clusterInAppStore = useClusterInAppStore();

    if (!token) {
        errorStore.setError({
            errorType: 'GLOBAL_ERROR',
            errorAction: 'GO_MAIN',
            errorMessage: '일시적인 오류가 발생했습니다. 앱을 다시 실행해주세요.',
        });
        return;
    }

    setCookieForDay('tokenValue', token);
    setCookieForDay('declareDateTimeValue', declareDateTime ?? '');
    setCookieForDay('osValue', os ?? '');
    clusterInAppStore.isReadyInAppWebView = true;
    clusterInAppStore.setInitData();
};

(window as any).setToken = (token: string) => {
    applyNativeTokenData(token);
};

(window as any).receiveJsonData = (data: string) => {
    try {
        const { token, declareDateTime, OS } = JSON.parse(data);
        applyNativeTokenData(token, declareDateTime, OS);
    } catch (e) {
        window.__nativeBridgeCalled = true;
        console.error('receiveJsonData parse error', e);
    }
};

// Vue 마운트 이전에 WEBVIEW_READY를 송신해 네이티브가 토큰/쿠키 응답을 내려보낼
// 시간을 최대한 확보. App.vue onMounted에서 호출하던 시점은 첫 commit 이후라
// 콜드 스타트 race를 유발했음.
callWebViewReady();

Sentry.init({
    app,
    dsn: 'https://1c210ff5a2d7b5fdf4df02d94c895840@o4509484388646912.ingest.us.sentry.io/4509933511376896',
    integrations: [
        Sentry.browserTracingIntegration({ router, routeLabel: 'path' }),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0, // 퍼포먼스 트래킹 사용 비율 (0~1)
    environment: import.meta.env.MODE, // dev / production 구분
    beforeSend(event) {
        // 루프 안에서 발화 가능해 quota 소모 위험이 큰 메시지는 1%만 샘플링
        const msg = event.message ?? event.exception?.values?.[0]?.value ?? '';
        const floodProne = [
            '픽셀 데이터 읽기 오류',
            '이미지 그리기 오류',
            'Invalid decimalPlace value',
            'Invalid tick value',
        ];
        if (floodProne.some(m => msg.includes(m)) && Math.random() > 0.01) {
            return null;
        }

        // 모든 이벤트에 토큰 상태 태그 자동 첨부 (디버깅용)
        event.tags = {
            ...event.tags,
            cookie_has_token: !!Cookies.get('tokenValue'),
            bridge_called: !!window.__nativeBridgeCalled,
        };
        return event;
    },
});

// AIDEV-NOTE: trend-report 스토어가 감지한 서버 응답 이상을 Sentry 로 보낸다.
// 공유 패키지(@common/trend-report-common)가 @sentry/vue 를 직접 참조하면 Sentry 를 쓰지
// 않는 admin/share-report 번들까지 SDK 를 떠안게 되어, 전송만 여기서 주입한다.
// Sentry.init 이후에 등록해야 첫 신고부터 정상 전송된다.
setAnomalyReporter((message, { kind, scope, extra }) => {
    Sentry.captureMessage(message, {
        level: 'error',
        tags: { trend_report_anomaly: kind, trend_report_scope: scope },
        extra: { scope, ...extra },
    });
});

app.use(router);
app.use(i18n);
app.use(VueDOMPurifyHTML, domPurifySettings);
app.mount('#app');
